import React, { useState, useEffect } from 'react';
import { FiPlus, FiEdit2, FiTrash2, FiUserCheck, FiShield } from 'react-icons/fi';
import useEntity from '../hooks/useEntity';
import Modal from '../components/ui/Modal';
import PasswordInput from '../components/ui/PasswordInput';
import api from '../utils/api';
import { useAuth } from '../contexts/AuthContext';
import { MODULOS } from '../config/modulos';

const ACCIONES = ['ver', 'crear', 'editar', 'eliminar', 'enviar_email', 'eleccion_ia'];
const MODULO_IA = { clave: 'ia', nombre: 'Elección IA' };
const MODULOS_ASIGNABLES = [...MODULOS.filter((m) => !m.core), MODULO_IA];

const VACIO = {
  nombre: '', apellidos: '', email: '', password: '', telefono: '', puesto: '',
  rol: 'admin', activo: true, permisos: [], vacaciones: { diasAnuales: 22 },
};

export default function Equipo() {
  const { usuario: yo } = useAuth();
  const { items, cargando, error, crear, actualizar, eliminar } = useEntity('usuarios');
  const [modal, setModal] = useState(false);
  const [editando, setEditando] = useState(null);
  const [form, setForm] = useState(VACIO);
  const [guardando, setGuardando] = useState(false);
  const [usuarioEnOtraEmpresa, setUsuarioEnOtraEmpresa] = useState(false);
  const [comprobandoEmail, setComprobandoEmail] = useState(false);

  // Solo empleados de la empresa (excluye superadmins globales)
  const usuarios = items.filter((u) => u.rol !== 'superadmin');
  const emailValido = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(String(form.email || '').trim());
  const altaBloqueada = !editando && (!emailValido || comprobandoEmail || usuarioEnOtraEmpresa);

  useEffect(() => {
    if (editando) {
      setUsuarioEnOtraEmpresa(false);
      return;
    }

    const email = String(form.email || '').trim().toLowerCase();
    if (!email || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      setUsuarioEnOtraEmpresa(false);
      setComprobandoEmail(false);
      return;
    }

    let cancelado = false;
    const comprobar = async () => {
      setComprobandoEmail(true);
      try {
        const { data } = await api.get('/usuarios/existe-en-otra-empresa', { params: { email } });
        if (!cancelado) setUsuarioEnOtraEmpresa(Boolean(data?.existeEnOtraEmpresa));
      } catch {
        if (!cancelado) setUsuarioEnOtraEmpresa(false);
      } finally {
        if (!cancelado) setComprobandoEmail(false);
      }
    };

    comprobar();
    return () => { cancelado = true; };
  }, [form.email, editando]);

  const abrirNuevo = () => { setEditando(null); setForm(VACIO); setModal(true); };
  const abrirEditar = (u) => {
    setEditando(u);
    setForm({
      ...VACIO, ...u, password: '',
      permisos: (u.permisos || []).map((p) => ({ modulo: p.modulo, acciones: [...p.acciones] })),
      vacaciones: { diasAnuales: u.vacaciones?.diasAnuales ?? 22 },
    });
    setModal(true);
  };

  const set = (campo) => (e) => setForm((f) => ({ ...f, [campo]: e.target.value }));

  const tienePermiso = (modulo, accion) =>
    form.permisos.some((p) => p.modulo === modulo && p.acciones.includes(accion));

  const togglePermiso = (modulo, accion) => {
    setForm((f) => {
      const permisos = [...f.permisos];
      const idx = permisos.findIndex((p) => p.modulo === modulo);
      if (idx === -1) {
        permisos.push({ modulo, acciones: [accion] });
      } else {
        const acc = permisos[idx].acciones;
        permisos[idx] = {
          modulo,
          acciones: acc.includes(accion) ? acc.filter((a) => a !== accion) : [...acc, accion],
        };
        if (permisos[idx].acciones.length === 0) permisos.splice(idx, 1);
      }
      return { ...f, permisos };
    });
  };

  const onGuardar = async (e) => {
    e.preventDefault();
    setGuardando(true);
    try {
      const datos = {
        nombre: form.nombre, apellidos: form.apellidos, email: form.email,
        telefono: form.telefono, puesto: form.puesto, rol: editando ? form.rol : 'admin', activo: form.activo,
        permisos: editando && form.rol === 'empleado' ? form.permisos : [],
        vacaciones: { diasAnuales: Number(form.vacaciones.diasAnuales) || 0 },
      };
      if (form.password) datos.password = form.password;
      if (editando) await actualizar(editando._id, datos);
      else {
        if (!usuarioEnOtraEmpresa && !form.password) { alert('La contraseña es obligatoria'); setGuardando(false); return; }
        datos.esUsuarioNuevo = !usuarioEnOtraEmpresa;
        await crear(datos);
      }
      setModal(false);
    } catch (err) {
      alert(err.response?.data?.error || 'Error al guardar');
    } finally {
      setGuardando(false);
    }
  };

  const onEliminar = async (u) => {
    if (!window.confirm(`¿Eliminar a "${u.nombre} ${u.apellidos}"?`)) return;
    try { await eliminar(u._id); } catch (err) { alert(err.response?.data?.error || 'No se pudo eliminar'); }
  };

  return (
    <div className="page">
      <div className="page-head">
        <div>
          <h1 className="page-title">Equipo</h1>
          <p className="page-subtitle">{usuarios.length} trabajador(es)</p>
        </div>
        <button className="btn btn-primary" onClick={abrirNuevo}><FiPlus /> Nuevo trabajador</button>
      </div>

      {error && <div className="alert-error">{error}</div>}
      {cargando ? (
        <p className="empty">Cargando…</p>
      ) : usuarios.length === 0 ? (
        <p className="empty"><FiUserCheck /> No hay trabajadores.</p>
      ) : (
        <div className="table-wrap">
          <table className="data-table">
            <thead>
              <tr><th>Nombre</th><th>Email</th><th>Puesto</th><th>Rol</th><th>Estado</th><th></th></tr>
            </thead>
            <tbody>
              {usuarios.map((u) => (
                <tr key={u._id}>
                  <td data-label="Nombre"><strong>{u.nombre} {u.apellidos}</strong></td>
                  <td data-label="Email">{u.email}</td>
                  <td data-label="Puesto">{u.puesto || '—'}</td>
                  <td data-label="Rol"><span className={`badge ${u.rol === 'admin' ? 'badge-info' : ''}`}>{u.rol}</span></td>
                  <td data-label="Estado"><span className={`badge ${u.activo ? 'badge-ok' : 'badge-danger'}`}>{u.activo ? 'Activo' : 'Inactivo'}</span></td>
                  <td className="acciones">
                    <button className="icon-btn" onClick={() => abrirEditar(u)} title="Editar"><FiEdit2 /></button>
                    {u._id !== yo?._id && <button className="icon-btn danger" onClick={() => onEliminar(u)} title="Eliminar"><FiTrash2 /></button>}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <Modal abierto={modal} onCerrar={() => setModal(false)} titulo={editando ? 'Editar trabajador' : 'Nuevo trabajador'} ancho={680}>
        <form onSubmit={onGuardar} className="form-grid">
          <label className="field"><span>Email *</span><input type="email" required value={form.email} onChange={set('email')} disabled={!!editando} /></label>
          {comprobandoEmail && !editando && <div className="alert-info span-2">Comprobando si este usuario ya estaba dado de alta en otra empresa…</div>}
          {usuarioEnOtraEmpresa && !editando && (
            <div className="alert-warning span-2">Este email ya pertenece a un usuario de otra empresa. Sus datos personales están bloqueados; solo puedes asignarle el rol y los permisos para esta empresa.</div>
          )}
          <label className="field"><span>Nombre *</span><input required value={form.nombre} onChange={set('nombre')} disabled={altaBloqueada} /></label>
          <label className="field"><span>Apellidos</span><input value={form.apellidos} onChange={set('apellidos')} disabled={altaBloqueada} /></label>
          <label className="field"><span>{editando ? 'Nueva contraseña' : usuarioEnOtraEmpresa ? 'Contraseña (mantiene acceso existente)' : 'Contraseña *'}</span><PasswordInput value={form.password} onChange={set('password')} placeholder={editando ? 'Dejar en blanco para no cambiar' : usuarioEnOtraEmpresa ? 'Se reutilizarán los datos de acceso existentes' : ''} disabled={altaBloqueada || !!editando} required={!editando && !usuarioEnOtraEmpresa} /></label>
          <label className="field"><span>Teléfono</span><input value={form.telefono} onChange={set('telefono')} disabled={altaBloqueada} /></label>
          <label className="field"><span>Puesto</span><input value={form.puesto} onChange={set('puesto')} disabled={altaBloqueada} /></label>
          <label className="field"><span>Rol</span>
            <select value={form.rol} onChange={set('rol')} disabled={!editando}>
              <option value="empleado">Empleado</option>
              <option value="admin">Administrador</option>
            </select>
          </label>
          <label className="field"><span>Días de vacaciones / año</span><input type="number" value={form.vacaciones.diasAnuales} onChange={(e) => setForm((f) => ({ ...f, vacaciones: { diasAnuales: e.target.value } }))} disabled={altaBloqueada} /></label>
          <label className="field checkbox span-2"><input type="checkbox" checked={form.activo} onChange={(e) => setForm((f) => ({ ...f, activo: e.target.checked }))} disabled={altaBloqueada} /><span>Usuario activo</span></label>

          {editando && form.rol === 'empleado' && (
            <div className="span-2">
              <h4 className="permisos-titulo"><FiShield /> Permisos por módulo</h4>
              <p className="muted">El administrador tiene acceso completo. Para empleados, marca las acciones permitidas.</p>
              <div className="table-wrap">
                <table className="data-table permisos-table">
                  <thead>
                    <tr><th>Módulo</th>{ACCIONES.map((a) => <th key={a}>{a === 'enviar_email' ? 'enviar email' : a}</th>)}</tr>
                  </thead>
                  <tbody>
                    {MODULOS_ASIGNABLES.map((m) => (
                      <tr key={m.clave}>
                        <td>{m.nombre}</td>
                        {ACCIONES.map((a) => (
                          <td key={a} style={{ textAlign: 'center' }}>
                            <input type="checkbox" checked={tienePermiso(m.clave, a)} onChange={() => togglePermiso(m.clave, a)} />
                          </td>
                        ))}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          <div className="form-actions span-2">
            <button type="button" className="btn" onClick={() => setModal(false)}>Cancelar</button>
            <button type="submit" className="btn btn-primary" disabled={guardando}>{guardando ? 'Guardando…' : 'Guardar'}</button>
          </div>
        </form>
      </Modal>
    </div>
  );
}
